import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { ServiceAdminSlug } from "./use-service-admin.ts";
import { VciCredentials } from "./VciCredentials.tsx";
import { IdpConsole } from "./IdpConsole.tsx";
import { AisConsole } from "./AisConsole.tsx";
import "./service-admin.css";

/**
 * The in-control-plane admin console for a hosted service (IDP / AIS / VCI). It replaces each
 * service's standalone server-rendered admin UI: the operator opens it by clicking the service on
 * the Overview KYA map, which routes to `/services/{slug}`. A tabbed console hosts that service's
 * admin surfaces, each reading the service's admin API through the BFF proxy.
 *
 * Tabs per service mirror the original admin UIs:
 *   - VCI: Credentials (explorer + debugger + lifecycle).
 *   - IDP: Registrations, Logs.
 *   - AIS: Agents, Logs, Monitor.
 */

interface Tab {
  readonly id: string;
  readonly label: string;
  readonly render: () => ReactNode;
}

function tabsFor(slug: ServiceAdminSlug): readonly Tab[] {
  switch (slug) {
    case "vci":
      return [
        { id: "dashboard", label: "Dashboard", render: () => <VciCredentials view="dashboard" /> },
        { id: "credentials", label: "Credentials", render: () => <VciCredentials view="credentials" /> },
      ];
    case "idp":
      return [
        { id: "dashboard", label: "Dashboard", render: () => <IdpConsole view="dashboard" /> },
        { id: "registrations", label: "Registrations", render: () => <IdpConsole view="registrations" /> },
        { id: "logs", label: "Logs", render: () => <IdpConsole view="logs" /> },
      ];
    case "ais":
      return [
        { id: "dashboard", label: "Dashboard", render: () => <AisConsole view="dashboard" /> },
        { id: "agents", label: "Agents", render: () => <AisConsole view="agents" /> },
        { id: "logs", label: "Logs", render: () => <AisConsole view="logs" /> },
        { id: "monitor", label: "Monitor", render: () => <AisConsole view="monitor" /> },
        { id: "signals", label: "Signals", render: () => <AisConsole view="signals" /> },
        { id: "governance", label: "Governance", render: () => <AisConsole view="governance" /> },
        { id: "policies", label: "Policies", render: () => <AisConsole view="policies" /> },
        { id: "policy-assignments", label: "Assignments", render: () => <AisConsole view="policy-assignments" /> },
        { id: "receipts", label: "Receipts", render: () => <AisConsole view="receipts" /> },
        { id: "receipt-chains", label: "Chains", render: () => <AisConsole view="receipt-chains" /> },
        { id: "receipt-keys", label: "Receipt keys", render: () => <AisConsole view="receipt-keys" /> },
        { id: "gateways", label: "Gateways", render: () => <AisConsole view="gateways" /> },
      ];
  }
}

/**
 * Keyed by slug so switching service (e.g. via the strip's service switcher) remounts the body and
 * resets the local tab state to the default Dashboard tab, rather than leaking a stale tab id.
 */
export function ServiceAdminConsole(props: { slug: ServiceAdminSlug }): ReactNode {
  return <ConsoleBody key={props.slug} slug={props.slug} />;
}

function ConsoleBody(props: { slug: ServiceAdminSlug }): ReactNode {
  const tabs = tabsFor(props.slug);
  const [active, setActive] = useState<string>(tabs[0]?.id ?? "");
  const current = tabs.find((t) => t.id === active) ?? tabs[0];
  const tablistRef = useRef<HTMLDivElement | null>(null);

  const moveTab = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;
    event.preventDefault();
    const tab = tabs[next];
    if (tab === undefined) return;
    setActive(tab.id);
    const buttons = tablistRef.current?.querySelectorAll<HTMLButtonElement>("[role='tab']");
    buttons?.[next]?.focus();
  };

  return (
    <div className="svc-console">
      <div ref={tablistRef} className="svc-console__tabs" role="tablist" aria-label={`${props.slug.toUpperCase()} admin sections`}>
        {tabs.map((t, index) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === active}
            tabIndex={t.id === active ? 0 : -1}
            className="svc-console__tab"
            data-active={t.id === active ? "true" : undefined}
            onKeyDown={(event) => moveTab(event, index)}
            onClick={() => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="svc-console__panel" role="tabpanel">
        {current?.render()}
      </div>
    </div>
  );
}
